import 'regenerator-runtime/runtime';
import { call, put, fork, takeEvery, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { parseError } from 'logic/helpers';
import { getIntl } from 'i18n/IntlProvider';
import history from 'logic/history';
import api from 'api';
import PATHS from 'constants/paths';
import * as actionCreators from './actionCreators';
import * as actions from './actions';

function notFoundError() {
  const intl = getIntl();
  return {
    error: {
      message: intl.formatMessage({
        id: 'complaints.notFound',
        defaultMessage: 'The complaint could not be found.',
      }),
    },
  };
}

function imageError() {
  const intl = getIntl();
  return {
    error: {
      message: intl.formatMessage({
        id: 'complaints.imageOperationFailed',
        defaultMessage:
          'Some image changes could not be saved. Check your images and edit again if needed.',
      }),
    },
  };
}

function conflictError(messageId, defaultMessage) {
  const intl = getIntl();
  return {
    error: {
      message: intl.formatMessage({
        id: messageId || 'complaints.conflict',
        defaultMessage:
          defaultMessage || 'This complaint was modified. Please review the updated version.',
      }),
    },
  };
}

export function* uploadFilesToComplaint(complaintId, files) {
  // eslint-disable-next-line no-restricted-syntax
  for (const file of files) {
    const { signedUrl, path } = yield call(api.uploadComplaintImage, complaintId, {
      fileName: file.name,
    });
    yield call(api.uploadToS3, signedUrl, file);
    yield call(api.confirmComplaintImage, complaintId, { path });
  }
}

// Guest: list
export function* fetchComplaintsRequest() {
  try {
    const data = yield call(api.fetchComplaints);
    yield put(actionCreators.fetchComplaintsSuccess(data.rows));
  } catch (e) {
    yield put(actionCreators.fetchComplaintsFailure(parseError(e)));
  }
}

// Guest: detail
export function* fetchComplaintRequest(req) {
  try {
    const { id } = req.payload;
    const data = yield call(api.fetchComplaint, id);
    yield put(actionCreators.fetchComplaintSuccess(data));
  } catch (e) {
    yield put(
      actionCreators.fetchComplaintFailure(e.status === 404 ? notFoundError() : parseError(e)),
    );
  }
}

// Guest: create
export function* createComplaintRequest(req) {
  try {
    const { data, files } = req.payload;
    const complaint = yield call(api.createComplaint, data);
    let imageWarning = false;
    if (files?.length) {
      try {
        yield call(uploadFilesToComplaint, complaint.id, files);
      } catch (uploadErr) {
        imageWarning = true;
      }
    }
    yield put(actionCreators.createComplaintSuccess(complaint));
    if (imageWarning) {
      yield put(actionCreators.complaintImageWarning(imageError()));
    }
    yield call(delay, 500);
    yield call([history, history.push], `${PATHS.complaints.root}/${complaint.id}`);
  } catch (e) {
    if (e.status === 409 && e.data?.complaint_id) {
      yield call(
        [history, history.push],
        `${PATHS.complaints.root}/${e.data.complaint_id}?existing=1`,
      );
      return;
    }
    yield put(actionCreators.createComplaintFailure(parseError(e)));
  }
}

// Guest: update
export function* updateComplaintRequest(req) {
  try {
    const { id, data, files, removedImageIds } = req.payload;
    const complaint = yield call(api.updateComplaint, id, data);
    let imageWarning = false;
    if (removedImageIds?.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const imageId of removedImageIds) {
        try {
          yield call(api.deleteComplaintImage, id, imageId);
        } catch (deleteErr) {
          imageWarning = true;
        }
      }
    }
    if (files?.length) {
      try {
        yield call(uploadFilesToComplaint, id, files);
      } catch (uploadErr) {
        imageWarning = true;
      }
    }
    yield put(actionCreators.updateComplaintSuccess(complaint));
    if (imageWarning) {
      yield put(actionCreators.complaintImageWarning(imageError()));
    }
    yield call(delay, 500);
    yield call([history, history.push], `${PATHS.complaints.root}/${id}`);
  } catch (e) {
    if (e.status === 409) {
      yield put(actionCreators.complaintConflict());
      yield put(actionCreators.fetchComplaintRequest({ id: req.payload.id }));
      yield put(
        actionCreators.updateComplaintFailure(
          conflictError(
            'complaints.conflict.guestEdit',
            'The host has responded to the complaint. You can no longer edit your complaint.',
          ),
        ),
      );
    } else {
      yield put(actionCreators.updateComplaintFailure(parseError(e)));
    }
  }
}

// Guest: withdraw
export function* withdrawComplaintRequest(req) {
  try {
    const { id, updated_at } = req.payload;
    yield call(api.withdrawComplaint, id, { updated_at });
    yield put(actionCreators.withdrawComplaintSuccess());
    yield call(delay, 500);
    yield call([history, history.push], PATHS.complaints.root);
  } catch (e) {
    if (e.status === 409) {
      yield put(actionCreators.complaintConflict());
      yield put(actionCreators.fetchComplaintRequest({ id: req.payload.id }));
      yield put(actionCreators.withdrawComplaintFailure(conflictError()));
    } else {
      yield put(actionCreators.withdrawComplaintFailure(parseError(e)));
    }
  }
}

// Guest: respond to counter-offer
export function* respondComplaintRequest(req) {
  try {
    const { id, data } = req.payload;
    const complaint = yield call(api.respondComplaint, id, data);
    yield put(actionCreators.respondComplaintSuccess(complaint));
  } catch (e) {
    if (e.status === 409) {
      yield put(actionCreators.complaintConflict());
      yield put(actionCreators.fetchComplaintRequest({ id: req.payload.id }));
      yield put(actionCreators.respondComplaintFailure(conflictError()));
    } else {
      yield put(actionCreators.respondComplaintFailure(parseError(e)));
    }
  }
}

// Guest: upload image
export function* uploadComplaintImageRequest(req) {
  try {
    const { id, fileName } = req.payload;
    const result = yield call(api.uploadComplaintImage, id, { fileName });
    yield put(actionCreators.uploadComplaintImageSuccess(result));
  } catch (e) {
    yield put(actionCreators.uploadComplaintImageFailure(parseError(e)));
  }
}

// Host: list
export function* fetchHostComplaintsRequest() {
  try {
    const data = yield call(api.fetchHostComplaints);
    yield put(actionCreators.fetchHostComplaintsSuccess(data.rows));
  } catch (e) {
    yield put(actionCreators.fetchHostComplaintsFailure(parseError(e)));
  }
}

// Host: detail
export function* fetchHostComplaintRequest(req) {
  try {
    const { id } = req.payload;
    const data = yield call(api.fetchHostComplaint, id);
    yield put(actionCreators.fetchHostComplaintSuccess(data));
  } catch (e) {
    yield put(
      actionCreators.fetchHostComplaintFailure(e.status === 404 ? notFoundError() : parseError(e)),
    );
  }
}

// Host: respond
export function* hostRespondComplaintRequest(req) {
  try {
    const { id, data } = req.payload;
    const complaint = yield call(api.hostRespondComplaint, id, data);
    yield put(actionCreators.hostRespondComplaintSuccess(complaint));
  } catch (e) {
    if (e.status === 409) {
      yield put(actionCreators.complaintConflict());
      yield put(actionCreators.fetchHostComplaintRequest({ id: req.payload.id }));
      yield put(
        actionCreators.hostRespondComplaintFailure(
          conflictError(
            'complaints.conflict.hostRespond',
            'The guest has edited the complaint. Please review the updated version before responding.',
          ),
        ),
      );
    } else {
      yield put(actionCreators.hostRespondComplaintFailure(parseError(e)));
    }
  }
}

export default [
  fork(takeEvery, actions.FETCH_COMPLAINTS_REQUEST, fetchComplaintsRequest),
  fork(takeEvery, actions.FETCH_COMPLAINT_REQUEST, fetchComplaintRequest),
  fork(takeLatest, actions.CREATE_COMPLAINT_REQUEST, createComplaintRequest),
  fork(takeLatest, actions.UPDATE_COMPLAINT_REQUEST, updateComplaintRequest),
  fork(takeLatest, actions.WITHDRAW_COMPLAINT_REQUEST, withdrawComplaintRequest),
  fork(takeLatest, actions.RESPOND_COMPLAINT_REQUEST, respondComplaintRequest),
  fork(takeLatest, actions.UPLOAD_COMPLAINT_IMAGE_REQUEST, uploadComplaintImageRequest),
  fork(takeEvery, actions.FETCH_HOST_COMPLAINTS_REQUEST, fetchHostComplaintsRequest),
  fork(takeEvery, actions.FETCH_HOST_COMPLAINT_REQUEST, fetchHostComplaintRequest),
  fork(takeLatest, actions.HOST_RESPOND_COMPLAINT_REQUEST, hostRespondComplaintRequest),
];
